import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { map, share } from 'rxjs/operators';
import { Charter } from 'src/app/shared/models/data/charter';
import { CharterSearchResults } from 'src/app/shared/models/data/charterSearchResults';
import { ClubSearchResults } from 'src/app/shared/models/data/clubSearchResults';
import { Owner } from 'src/app/shared/models/data/owner';
import { OwnerSearchResults } from 'src/app/shared/models/data/ownerSearchResults';
import { ProtectionClub } from 'src/app/shared/models/data/protectionClub';
import { Representative } from 'src/app/shared/models/data/representative';
import { RepresentativeSearchResults } from 'src/app/shared/models/data/representativeSearchResults';
import { Ship } from 'src/app/shared/models/data/ship';
import { ShipSearchResults } from 'src/app/shared/models/data/shipSearchResults';
import { SearchParams } from 'src/app/shared/models/searchParams';
import { environment } from 'src/environments/environment';
import { ErrorService } from '../error/error.service';
import { ApiBaseService } from './api-base.service';

@Injectable({
  providedIn: 'root'
})
export class MarineApiService extends ApiBaseService {

  baseUrl = environment.backend_host + '/api/marine';

  constructor(
    public http: HttpClient,
    protected errorService: ErrorService,
    protected spinner: NgxSpinnerService) {
    super(spinner, errorService);
  }

  // SHIPS
  public getShips = (params: SearchParams): Observable<ShipSearchResults> => {
    const p = this.serializeParamsObj(params, new HttpParams());
    return this.http.get<any>(`${this.baseUrl}/ships`, { params: p })
      .pipe(map(this.deserializeAs(ShipSearchResults)));
  }

  public getShip = (id: number): Observable<Ship> => {
    return this.http.get<any>(`${this.baseUrl}/ships/${id}`)
      .pipe(map(this.deserializeAs(Ship)));
  }

  public updateShip = (ship: Ship): Observable<Ship> => {
    return this.http.put<any>(`${this.baseUrl}/ships/${ship.id}`, this.serialize(ship))
      .pipe(map(this.deserializeAs(Ship)));
  }

  public createShip = (ship: Ship): Observable<Ship> => {
    return this.http.post<any>(`${this.baseUrl}/ships`, this.serialize(ship))
      .pipe(map(this.deserializeAs(Ship)));
  }

  public deleteShip = (ship: Ship): Observable<any> => {
    return this.http.delete<any>(`${this.baseUrl}/ships/${ship.id}`)
      .pipe(share());
  }

  // CLUBS
  public getClubs = (params: SearchParams): Observable<ClubSearchResults> => {
    const p = this.serializeParamsObj(params, new HttpParams());
    return this.http.get<any>(`${this.baseUrl}/clubs`, { params: p })
      .pipe(map(this.deserializeAs(ClubSearchResults)));
  }

  public getClub = (id: number): Observable<ProtectionClub> => {
    return this.http.get<any>(`${this.baseUrl}/clubs/${id}`)
      .pipe(map(this.deserializeAs(ProtectionClub)));
  }

  public updateClub = (club: ProtectionClub): Observable<ProtectionClub> => {
    return this.http.put<any>(`${this.baseUrl}/clubs/${club.id}`, this.serialize(club))
      .pipe(map(this.deserializeAs(ProtectionClub)));
  }

  public createClub = (club: ProtectionClub): Observable<ProtectionClub> => {
    return this.http.post<any>(`${this.baseUrl}/clubs`, this.serialize(club))
      .pipe(map(this.deserializeAs(ProtectionClub)));
  }

  public deleteClub = (club: ProtectionClub): Observable<any> => {
    return this.http.delete<any>(`${this.baseUrl}/clubs/${club.id}`)
      .pipe(share());
  }

  // Owners
  public getOwners = (params: SearchParams): Observable<OwnerSearchResults> => {
    const p = this.serializeParamsObj(params, new HttpParams());
    return this.http.get<any>(`${this.baseUrl}/owners`, { params: p })
      .pipe(map(this.deserializeAs(OwnerSearchResults)));
  }

  public getOwner = (id: number): Observable<Owner> => {
    return this.http.get<any>(`${this.baseUrl}/owners/${id}`)
      .pipe(map(this.deserializeAs(Owner)));
  }

  public updateOwner = (owner: Owner): Observable<Owner> => {
    return this.http.put<any>(`${this.baseUrl}/owners/${owner.id}`, this.serialize(owner))
      .pipe(map(this.deserializeAs(Owner)));
  }

  public createOwner = (owner: Owner): Observable<Owner> => {
    return this.http.post<any>(`${this.baseUrl}/owners`, this.serialize(owner))
      .pipe(map(this.deserializeAs(Owner)));
  }

  public deleteOwner = (owner: Owner): Observable<any> => {
    return this.http.delete<any>(`${this.baseUrl}/owners/${owner.id}`)
      .pipe(share());
  }

  // Charters
  public getCharters = (params: SearchParams): Observable<CharterSearchResults> => {
    const p = this.serializeParamsObj(params, new HttpParams());
    return this.http.get<any>(`${this.baseUrl}/charters`, { params: p })
      .pipe(map(this.deserializeAs(CharterSearchResults)));
  }

  public getCharter = (id: number): Observable<Charter> => {
    return this.http.get<any>(`${this.baseUrl}/charters/${id}`)
      .pipe(map(this.deserializeAs(Charter)));
  }

  public updateCharter = (charter: Charter): Observable<Charter> => {
    return this.http.put<any>(`${this.baseUrl}/charters/${charter.id}`, this.serialize(charter))
      .pipe(map(this.deserializeAs(Charter)));
  }

  public createCharter = (charter: Charter): Observable<Charter> => {
    return this.http.post<any>(`${this.baseUrl}/charters`, this.serialize(charter))
      .pipe(map(this.deserializeAs(Charter)));
  }

  public deleteCharter = (charter: Charter): Observable<any> => {
    return this.http.delete<any>(`${this.baseUrl}/charters/${charter.id}`)
      .pipe(share());
  }

  // Representatives
  public getRepresentatives = (params: SearchParams): Observable<RepresentativeSearchResults> => {
    const p = this.serializeParamsObj(params, new HttpParams());
    return this.http.get<any>(`${this.baseUrl}/reps`, { params: p })
      .pipe(map(this.deserializeAs(RepresentativeSearchResults)));
  }

  public getRepresentative = (id: number): Observable<Representative> => {
    return this.http.get<any>(`${this.baseUrl}/reps/${id}`)
      .pipe(map(this.deserializeAs(Representative)));
  }

  public updateRepresentative = (representative: Representative): Observable<Representative> => {
    return this.http.put<any>(`${this.baseUrl}/reps/${representative.id}`, this.serialize(representative))
      .pipe(map(this.deserializeAs(Representative)));
  }

  public createRepresentative = (representative: Representative): Observable<Representative> => {
    return this.http.post<any>(`${this.baseUrl}/reps`, this.serialize(representative))
      .pipe(map(this.deserializeAs(Representative)));
  }

  public deleteRepresentative = (representative: Representative): Observable<any> => {
    return this.http.delete<any>(`${this.baseUrl}/reps/${representative.id}`)
      .pipe(share());
  }
}
